import React, { useEffect, useState } from 'react';
import {
  FeatureFlagKey,
  isNumeric,
  useFeatureFlagEnabled,
  checkHtmlIsOnlyWhiteSpace,
  trimHtml,
} from '@applyboard/ui-utils';
import { Flex, Text } from '@applyboard/crystal-ui';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  TextField,
  GRAY,
  BLUE,
  Field,
  SubmitButton,
  useFormValue,
  useSetFormValue,
  ValidateButton,
  FormModal,
} from '@applyboard/ui-components';

import { ArrowRightOutlineIcon, ArrowLeftOutlineIcon } from '@applyboard/ui-icons';
import styled from '@emotion/styled';
import { ApplicationStage, useApplicationStages } from '~/data/applicationStages';
import { CreateRequirementForm } from '~/data/requirements';
import { DropdownField } from '~/components/DropdownField';
import { useCurrencies } from '~/data/currencies';
import {
  useRequirementCategoriesByStageId,
  RequirementCategory,
} from '~/data/requirementCategories';
import {
  RequirementClassification,
  useRequirementClassifications,
  Classifications,
} from '~/data/requirementClassifications';
import { usePaymentRequirementCategories } from '~/data/paymentRequirementCategories';
import { ExternalLinksField } from './ExternalLinksField';
import { FileCategoriesField } from './FileCategoriesField';
import { QuestionsField, validateQuestions } from './QuestionsField';
import { FileUploadField } from './FileUploadField';
import { TextAreaField } from './TextAreaField';
import { RichTextAreaField } from './RichTextAreaField';
import {
  useLegacyRequirementFileCategories,
  RequirementFileCategory,
} from '~/data/requirementFileCategories';

export interface LegacyRequirementModalProps {
  open?: boolean;
  onClose?: () => void;
  onSubmit?: (requirement: CreateRequirementForm) => void;
  saving?: boolean;
  hiddenRequirementTypes?: Array<'document' | 'questions' | 'payment' | 'receipt' | 'disclaimer'>;
  title?: string;
  defaultValues?: Partial<CreateRequirementForm>;
  saveButtonLabel?: string;
  lockedFields?: Array<
    | 'requirementTemplateType'
    | 'earlyAccess'
    | 'requirementCategory'
    | 'currency'
    | 'amount'
    | 'paymentRequirementCategoryId'
    | 'stage'
    | 'plagiarism'
    | 'classification'
    | null
  >;
  hiddenFields?: Array<'optional' | 'earlyAccess' | 'evaluationRecipeId'>;
  loading?: boolean;
}

export function LegacyRequirementModal(props: LegacyRequirementModalProps) {
  const defaultValues = {
    classification: Classifications.APPLICANT_REQUIREMENTS,
    stage: '',
    description: '',
    internalDescription: '',
    internalTitle: '',
    title: '',
    earlyAccess: false,
    optional: false,
    applyboardRequirement: false,
    applyproofCompatible: false,
    official: false,
    requirementCategory: '',
    requiresNotarization: false,
    plagiarismEnabled: false,
    maxPlagiarismScore: null,
    maxPlagiarismWordCount: null,
    minPlagiarismWordCount: null,
    maxPlagiarismCharCount: null,
    minPlagiarismCharCount: null,
    paymentRequirementCategoryId: undefined,
    currency: undefined,
    amount: '0',
    receiptCurrency: undefined,
    receiptAmount: '0',
    flywire: false,
    fileCategories: '*',
    questions: [],
    externalLinks: [],
    files: [],
    evaluationRecipeId: undefined,
    ...(props.defaultValues || {}),
  };

  const [step, setStep] = useState(1);
  useEffect(() => {
    if (props.open) {
      setStep(1);
    }
  }, [props.open]);

  return (
    <FormModal
      loading={props.loading}
      title={props.title || 'Requirement Details'}
      open={props.open}
      handleClose={props.onClose}
      onSubmit={requirement => {
        props.onSubmit({
          ...requirement,
          fileCategories: requirement.fileCategories === '*' ? [] : requirement.fileCategories,
          description: trimHtml(requirement.description),
          internalDescription: requirement.internalDescription?.trim(),
        });
      }}
      defaultValues={defaultValues}
      footerButtons={() => (
        <>
          {step === 2 && (
            <Button
              onClick={() => {
                setStep(1);
              }}
              startIcon={<ArrowLeftOutlineIcon />}
              style={{ borderTop: `1px solid ${GRAY[18]}` }}
              variant="text"
              color="secondary"
            >
              Back
            </Button>
          )}
          {step === 1 && (
            <ValidateButton
              as={Button}
              onValid={() => {
                setStep(2);
              }}
              endIcon={<ArrowRightOutlineIcon />}
            >
              Next
            </ValidateButton>
          )}
          {step === 2 && (
            <SubmitButton
              as={Button}
              loading={props.saving}
              disabled={props.saving}
              type="submit"
              disableIfValuesUnchanged
            >
              {props.saveButtonLabel ?? 'Create'}
            </SubmitButton>
          )}
        </>
      )}
    >
      <RequirementForm
        step={step}
        hiddenRequirementTypes={props.hiddenRequirementTypes}
        lockedFields={props.lockedFields}
        hiddenFields={props.hiddenFields}
      />
    </FormModal>
  );
}

function RequirementForm(props: {
  step: number;
  hiddenRequirementTypes?: Array<'document' | 'questions' | 'payment' | 'receipt' | 'disclaimer'>;
  lockedFields?: LegacyRequirementModalProps['lockedFields'];
  hiddenFields?: LegacyRequirementModalProps['hiddenFields'];
}) {
  const { applicationStages } = useApplicationStages();
  const stageName = useFormValue('stage');
  const stageId = applicationStages.find(stage => stage.value === stageName)?.id;
  const { requirementCategories } = useRequirementCategoriesByStageId({ stageId });
  const { requirementClassifications } = useRequirementClassifications();
  const { requirementFileCategories } = useLegacyRequirementFileCategories();

  return (
    <>
      {props.step === 1 && (
        <Step1
          applicationStages={applicationStages}
          lockedFields={props.lockedFields}
          hiddenFields={props.hiddenFields}
          requirementClassifications={requirementClassifications}
        />
      )}
      {props.step === 2 && (
        <Step2
          requirementCategories={requirementCategories}
          requirementFileCategories={requirementFileCategories}
          hiddenRequirementTypes={props.hiddenRequirementTypes}
          lockedFields={props.lockedFields}
          hiddenFields={props.hiddenFields}
        />
      )}
    </>
  );
}

function Step1(props: {
  applicationStages: ApplicationStage[];
  lockedFields: LegacyRequirementModalProps['lockedFields'];
  hiddenFields?: LegacyRequirementModalProps['hiddenFields'];
  requirementClassifications: RequirementClassification[];
}) {
  const setFormValue = useSetFormValue();
  const optional = useFormValue('optional');
  const earlyAccess = useFormValue('earlyAccess');
  const classification = useFormValue('classification');

  const earlyAccessEnabled = useFeatureFlagEnabled(FeatureFlagKey.ApplicationEarlyAccessMvp);

  return (
    <>
      <Field
        as={DropdownField}
        name="classification"
        label="Classification"
        options={props.requirementClassifications.map(requirementClassification => ({
          label: requirementClassification.displayName,
          value: requirementClassification.id,
        }))}
        mb={1}
        helperText={
          classification === Classifications.SCHOOL_REQUIREMENTS
            ? 'This requirement will be shown under the Actions Tab on the application page.'
            : null
        }
        disabled={props.lockedFields?.includes('classification')}
        required
      />
      <Box ml={1} display="flex" flexDirection="column">
        {earlyAccessEnabled && !props.hiddenFields?.includes('earlyAccess') && (
          <Field
            as={CheckboxField}
            label="Early Access"
            name="earlyAccess"
            helperText={earlyAccess ? 'Selecting Early Access Disables Optional' : ''}
            disabled={optional || props.lockedFields?.includes('earlyAccess')}
          />
        )}
        {!props.hiddenFields?.includes('optional') && (
          <Field
            as={CheckboxField}
            label="Optional"
            name="optional"
            helperText={
              optional &&
              !props.lockedFields?.includes('earlyAccess') &&
              !props.hiddenFields?.includes('earlyAccess')
                ? 'Selecting Optional Disables Early Access'
                : ''
            }
            disabled={earlyAccess}
          />
        )}
        <Field as={CheckboxField} label="ApplyBoard Requirement" name="applyboardRequirement" />
        <Field as={CheckboxField} label="ApplyProof Compatible" name="applyproofCompatible" />
        <Field as={CheckboxField} label="Student Records" name="official" />
      </Box>
      <Field
        as={DropdownField}
        name="stage"
        label="Stage"
        required
        onChange={() => {
          setFormValue('requirementCategory', '');
        }}
        options={props.applicationStages}
        mb={2}
        disabled={props.lockedFields?.includes('stage')}
      />
      <Field
        as={TextField}
        name="internalTitle"
        label="Internal Title"
        variant="outlined"
        fullWidth
        mb={2}
        required
      />
      <Field
        as={TextAreaField}
        name="internalDescription"
        label="Internal Description"
        mb={2}
        maxLength={2500}
      />
      <Field
        as={TextField}
        name="title"
        label="External Title"
        variant="outlined"
        fullWidth
        mb={1}
        required
      />
      <Field
        as={RichTextAreaField}
        name="description"
        label="External Description"
        required
        validate={value => {
          if (checkHtmlIsOnlyWhiteSpace(value)) {
            return 'This field is required';
          }

          return true;
        }}
        maxLength={2500}
      />
      <Field as={ExternalLinksField} name="externalLinks" mt={2} mb={2} />
      <Field as={FileUploadField} name="files" />
    </>
  );
}

function Step2(props: {
  requirementCategories: RequirementCategory[];
  requirementFileCategories: RequirementFileCategory[];
  hiddenRequirementTypes?: Array<'document' | 'questions' | 'payment' | 'receipt' | 'disclaimer'>;
  lockedFields?: LegacyRequirementModalProps['lockedFields'];
  hiddenFields?: LegacyRequirementModalProps['hiddenFields'];
}) {
  const requirementTemplateType = useFormValue('requirementTemplateType');
  const setFormValue = useSetFormValue();

  return (
    <>
      <Field
        as={DropdownField}
        name="requirementTemplateType"
        label="Type"
        onChange={() => {
          setFormValue('requirementCategory', '');
        }}
        options={[
          {
            value: 'document' as const,
            label: 'Document',
          },
          {
            value: 'questions' as const,
            label: 'Questions',
          },
          {
            value: 'payment' as const,
            label: 'Payment',
          },
          {
            value: 'receipt' as const,
            label: 'Receipt',
          },
          {
            value: 'disclaimer' as const,
            label: 'Disclaimer',
          },
        ].filter(option => !props.hiddenRequirementTypes?.includes(option.value))}
        mb={2}
        required
        disabled={props.lockedFields?.includes('requirementTemplateType')}
      />
      {requirementTemplateType && (
        <Field
          as={DropdownField}
          name="requirementCategory"
          label="Requirement Category"
          options={props.requirementCategories.filter(
            category => category.categoryType === requirementTemplateType,
          )}
          mb={2}
          disabled={props.lockedFields?.includes('requirementCategory')}
          required
        />
      )}
      {requirementTemplateType === 'document' && (
        <DocumentForm
          requirementFileCategories={props.requirementFileCategories}
          lockedFields={props.lockedFields}
        />
      )}
      {requirementTemplateType === 'payment' && <PaymentForm lockedFields={props.lockedFields} />}
      {requirementTemplateType === 'questions' && <QuestionsForm />}
      {requirementTemplateType === 'receipt' && <ReceiptForm />}
    </>
  );
}

function DocumentForm(props: {
  requirementFileCategories: RequirementFileCategory[];
  lockedFields: LegacyRequirementModalProps['lockedFields'];
}) {
  const plagiarismLocked = props.lockedFields?.includes('plagiarism');
  const fileCategories = useFormValue('fileCategories');
  const setFormValue = useSetFormValue();

  const plagiarismFlagEnabled = useFeatureFlagEnabled(FeatureFlagKey.ApplicationsPlagiarismCheck);
  const maxPlagiarismScore = useFormValue('maxPlagiarismScore');
  const [plagiarismEnabled, setPlagiarismEnabled] = useState(isNumeric(maxPlagiarismScore));

  const maxPlagiarismWordCount = useFormValue('maxPlagiarismWordCount');
  const minPlagiarismWordCount = useFormValue('minPlagiarismWordCount');
  const maxPlagiarismCharCount = useFormValue('maxPlagiarismCharCount');
  const minPlagiarismCharCount = useFormValue('minPlagiarismCharCount');
  const charCountDisabled = maxPlagiarismWordCount || minPlagiarismWordCount;
  const wordCountDisabled = maxPlagiarismCharCount || minPlagiarismCharCount;

  return (
    <div>
      <Field as={CheckboxField} name="requiresNotarization" label="Requires Notarization" />
      <Field
        as={NumberOrNullField}
        checkedValue={1}
        name="fileUploadLimit"
        label="Single File Upload Limit"
      />
      {plagiarismFlagEnabled && (
        <FormControlLabel
          control={
            <Checkbox
              disabled={plagiarismLocked}
              checked={plagiarismEnabled}
              onChange={(e, value) => {
                setPlagiarismEnabled(value);
                if (!value) {
                  setFormValue('maxPlagiarismWordCount', null);
                  setFormValue('minPlagiarismWordCount', null);
                  setFormValue('maxPlagiarismCharCount', null);
                  setFormValue('minPlagiarismCharCount', null);
                  setFormValue('maxPlagiarismScore', null);
                }
                setFormValue(
                  'fileCategories',
                  plagiarismEnabled
                    ? fileCategories
                    : fileCategories.filter(type => {
                        return props.requirementFileCategories.find(
                          fileCategory => fileCategory.value === type,
                        )?.supportsPlagiarismCheck;
                      }),
                );
              }}
            />
          }
          label="Plagiarism Enabled"
        />
      )}

      <Flex align="stretch" direction="column" gap={2}>
        {plagiarismEnabled && plagiarismFlagEnabled && (
          <>
            <Flex align="stretch" direction="row" gap={2}>
              <Field
                disabled={plagiarismLocked}
                as={TextField}
                name="maxPlagiarismScore"
                fullWidth
                required
                variant="outlined"
                label="Maximum Plagiarism Score"
                validate={value =>
                  validateMinMax(value, {
                    min: 0,
                    max: 100,
                    helperText: 'Must be between 0% and 100%',
                  })
                }
              />
            </Flex>
            {!(plagiarismLocked && wordCountDisabled) && (
              <>
                <Text contrast={wordCountDisabled ? 'low' : ''}>Word Count</Text>
                <Flex align="stretch" direction="row" gap={2}>
                  <Field
                    disabled={wordCountDisabled || plagiarismLocked}
                    as={TextField}
                    name="minPlagiarismWordCount"
                    fullWidth
                    variant="outlined"
                    label="Minimum"
                    validate={value =>
                      validateMinMax(value, {
                        max: maxPlagiarismWordCount,
                        helperText: 'Must be less than maximum',
                      })
                    }
                  />
                  <Field
                    disabled={wordCountDisabled || plagiarismLocked}
                    as={TextField}
                    name="maxPlagiarismWordCount"
                    fullWidth
                    variant="outlined"
                    label="Maximum"
                    validate={value =>
                      validateMinMax(value, {
                        min: minPlagiarismWordCount,
                        helperText: 'Must be less than maximum',
                      })
                    }
                  />
                </Flex>
              </>
            )}
            {!(plagiarismLocked && (charCountDisabled || wordCountDisabled)) && <Text>Or</Text>}
            {!(plagiarismLocked && charCountDisabled) && (
              <>
                <Text contrast={charCountDisabled ? 'low' : ''}>Character Count</Text>
                <Flex align="stretch" direction="row" gap={2}>
                  <Field
                    disabled={charCountDisabled || plagiarismLocked}
                    as={TextField}
                    name="minPlagiarismCharCount"
                    fullWidth
                    variant="outlined"
                    label="Minimum"
                    validate={value =>
                      validateMinMax(value, {
                        max: maxPlagiarismCharCount,
                        helperText: 'Must be less than maximum',
                      })
                    }
                  />
                  <Field
                    disabled={charCountDisabled || plagiarismLocked}
                    as={TextField}
                    name="maxPlagiarismCharCount"
                    fullWidth
                    variant="outlined"
                    label="Maximum"
                    validate={value =>
                      validateMinMax(value, {
                        min: minPlagiarismCharCount,
                        helperText: 'Must be less than maximum',
                      })
                    }
                  />
                </Flex>
              </>
            )}
          </>
        )}
        <Field
          as={FileCategoriesField}
          options={props.requirementFileCategories}
          plagiarismCheckEnabled={plagiarismEnabled}
          name="fileCategories"
          required
          helperText={
            plagiarismEnabled ? 'Enabling Plagiarism Check Limits Available File Choices' : ''
          }
          validate={value => {
            // We want to mark the field as missing if value is '*' because that
            // means the options haven't loaded from the api yet
            const hasNotLoadedOptionsYet = value === '*';
            if (hasNotLoadedOptionsYet) {
              return 'This field is required. Please wait for checkboxes to load...';
            }

            return true;
          }}
        />
      </Flex>
    </div>
  );
}

function PaymentForm(props: { lockedFields?: LegacyRequirementModalProps['lockedFields'] }) {
  const { paymentRequirementCategories } = usePaymentRequirementCategories();
  const { currencies } = useCurrencies();
  const paymentRequirementCategoryId = useFormValue('paymentRequirementCategoryId');
  const paymentRequirementCategoryDescription = !paymentRequirementCategoryId
    ? ''
    : paymentRequirementCategories.find(
        category => category.id === paymentRequirementCategoryId.toString(),
      )?.description;

  return (
    <div>
      <Field
        as={DropdownField}
        name="paymentRequirementCategoryId"
        label="Payment Requirement Category"
        options={paymentRequirementCategories.map(category => ({
          label: category.displayName,
          value: Number(category.id),
        }))}
        helperText={paymentRequirementCategoryDescription}
        mb={2}
        required
        disabled={props.lockedFields?.includes('paymentRequirementCategoryId')}
      />
      <Box display="flex">
        <Field
          as={DropdownField}
          name="currency"
          label="Currency"
          options={currencies.map(currency => ({
            label: currency.name,
            value: currency.code,
          }))}
          mr={0.5}
          required
          disabled={props.lockedFields?.includes('currency')}
        />
        <Field
          as={TextField}
          name="amount"
          variant="outlined"
          type="number"
          min="0"
          label="Amount"
          fullWidth
          ml={0.5}
          disabled={props.lockedFields?.includes('amount')}
        />
      </Box>
    </div>
  );
}

function QuestionsForm() {
  return <Field as={QuestionsField} name="questions" required validate={validateQuestions} />;
}

function ReceiptForm() {
  const { currencies } = useCurrencies();

  // TODO: when we change this dialog to support editing, we should check if we
  // need to support multiple currency amounts.  The API returns an array for
  // some reason and I don't know why.
  return (
    <>
      <Box display="flex" flexDirection="row" alignItems="flex-start" mb={1}>
        <Field
          as={DropdownField}
          name="receiptCurrency"
          label="Currency"
          options={currencies.map(currency => ({
            label: currency.name,
            value: currency.code,
          }))}
          mr={0.5}
          required
        />
        <Field
          as={TextField}
          name="receiptAmount"
          label="Amount"
          variant="outlined"
          type="number"
          min="0"
          fullWidth
          ml={0.5}
        />
      </Box>
      <Field as={CheckboxField} name="flywire" label="Flywire Payment Links" />
    </>
  );
}

const StyledFormHelperText = styled(FormHelperText)`
  &.MuiFormHelperText-root {
    color: ${BLUE[10]};
    margin: 0;
  }
`;

function CheckboxField(props: {
  label: string;
  value?: boolean;
  onChange: (newValue: boolean) => void;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
}) {
  return (
    <FormControl>
      <FormControlLabel
        control={
          <Checkbox
            onChange={(e, newValue) => props.onChange(newValue)}
            checked={props.value}
            disabled={props.disabled}
          />
        }
        label={props.label}
      />
      {props.helperText && (
        <StyledFormHelperText error={props.error}>{props.helperText}</StyledFormHelperText>
      )}
    </FormControl>
  );
}

function NumberOrNullField(props: {
  label: string;
  checkedValue: number;
  value?: number | null;
  onChange: (newValue: number | null) => void;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
}) {
  return (
    <CheckboxField
      label={props.label}
      value={Boolean(props.value)}
      onChange={newValue => {
        if (newValue) {
          props.onChange(props.checkedValue);
        } else {
          props.onChange(null);
        }
      }}
      helperText={props.helperText}
      error={props.error}
      disabled={props.disabled}
    />
  );
}

export function validateMinMax(
  value: string,
  {
    min,
    max,
    helperText,
  }: {
    min?: number;
    max?: number;
    helperText: string;
  },
) {
  if (value) {
    if (isNaN(value)) return 'Must be a number';
    if (min === null || min === undefined) min = 0;
    if (max === null || max === undefined) max = Number.MAX_SAFE_INTEGER;

    const valueConvertedToNumber = Number(value);
    return valueConvertedToNumber > Number(max) || valueConvertedToNumber < Number(min)
      ? helperText
      : true;
  }
  return true;
}
